/**
 * Item model for the Design tab's "modular items" editing mode.
 *
 * A section's `.md` is the single source of truth (no JSON sidecar). This
 * module is a *view* over that markdown: it parses the body into discrete
 * items (name + one-line slug + collapsible details, up to two levels of
 * group → child) and serialises an edited item tree back to plain markdown.
 *
 * The convention (round-trips losslessly):
 *
 *   # Section Title           ← H1 + any intro prose before the first H2 = `preamble`
 *
 *   ## Inventory              ← H2 = a top-level item (a GROUP if it has H3 children)
 *   _A backpack the user fills._   ← first italic line after a heading = that item's slug
 *
 *   Details markdown…         ← everything until the next heading = that item's body
 *
 *   ## Inventory Items        ← H2 with H3 children = a group
 *   ### Potion                ← H3 = a child item (leaf; never nests further)
 *   _Heals Health by 50pts._
 *   Details…
 *
 * Because the output is ordinary markdown (`##`/`###` + italic + prose), the
 * build-export and "Sync design" flows that read the section file see normal
 * prose — there is no new on-disk format to teach them about.
 */

export interface DesignItem {
  /** Stable-within-doc id (slug of name, deduped) — for React keys + reorder. */
  id: string;
  /** Heading text (the nominative function, e.g. "Inventory"). */
  name: string;
  /** One-line description (the italic line). Empty string when absent. */
  slug: string;
  /** Details markdown — excludes the heading + slug line. */
  body: string;
  /** H3 items under an H2 group. Always `[]` for H3 (max depth 2). */
  children: DesignItem[];
}

export interface ParsedSection {
  /** H1 + any prose before the first H2. Preserved byte-exact when there are
   *  no items, so opening a freeform section in item-mode is non-destructive. */
  preamble: string;
  items: DesignItem[];
}

// Heading line: capture the level (#count) and the trimmed text.
const HEADING_RE = /^(#{1,6})\s+(.*\S)\s*$/;
// A wholly-italic line: `_text_` or `*text*` (NOT `**bold**`).
const SLUG_RE = /^(?:_([^_].*?)_|\*([^*].*?)\*)$/;

interface HeadingMark {
  line: number;
  level: number;
  text: string;
}

/**
 * Mark the lines that fall inside a *properly-closed* fenced code block.
 *
 * Only balanced fences count: an opener with no matching closer (a malformed /
 * half-typed code block) is treated as ordinary text, so it can never swallow
 * the headings below it — items stay visible instead of silently vanishing on
 * the next parse. Closed fences still shield their `##`-looking contents.
 */
function fencedLines(lines: string[]): boolean[] {
  const fenced = new Array<boolean>(lines.length).fill(false);
  let openChar: string | null = null;
  let openLen = 0;
  let openLine = -1;
  for (let i = 0; i < lines.length; i += 1) {
    const m = /^\s*(`{3,}|~{3,})/.exec(lines[i]);
    if (!m) continue;
    const run = m[1];
    const char = run[0];
    const len = run.length;
    if (openChar === null) {
      openChar = char;
      openLen = len;
      openLine = i;
    } else if (char === openChar && len >= openLen) {
      // CommonMark: a closing fence uses the same char and is at least as long
      // as the opener — so a shorter ``` line inside a ```` block doesn't close
      // it early (and a ~~~ line never closes a ``` block).
      for (let j = openLine; j <= i; j += 1) fenced[j] = true;
      openChar = null;
      openLen = 0;
      openLine = -1;
    }
  }
  // An unterminated opener is intentionally left unmarked (treated as text).
  return fenced;
}

/** Locate `##`/`###` headings, ignoring any inside closed fenced code blocks. */
function findHeadings(lines: string[]): HeadingMark[] {
  const marks: HeadingMark[] = [];
  const fenced = fencedLines(lines);
  for (let i = 0; i < lines.length; i += 1) {
    if (fenced[i]) continue;
    const h = HEADING_RE.exec(lines[i]);
    if (h && (h[1].length === 2 || h[1].length === 3)) {
      marks.push({ line: i, level: h[1].length, text: h[2] });
    }
  }
  return marks;
}

function slugify(s: string): string {
  let out = '';
  let lastDash = false;
  for (const c of s.toLowerCase()) {
    if (/[a-z0-9]/.test(c)) {
      out += c;
      lastDash = false;
    } else if (!lastDash && out.length > 0) {
      out += '-';
      lastDash = true;
    }
  }
  return out.replace(/^-+|-+$/g, '');
}

/** Strip leading/trailing blank lines but preserve internal formatting. */
function trimBlankLines(text: string): string {
  return text.replace(/^\n+/, '').replace(/\s+$/, '');
}

/**
 * Split the lines under a heading (excluding the heading line itself) into its
 * slug (first wholly-italic line) and the remaining body markdown.
 */
function splitSlugAndBody(bodyLines: string[]): { slug: string; body: string } {
  let i = 0;
  while (i < bodyLines.length && bodyLines[i].trim() === '') i += 1;
  if (i < bodyLines.length) {
    const m = SLUG_RE.exec(bodyLines[i].trim());
    if (m) {
      const slug = (m[1] ?? m[2]).trim();
      return { slug, body: trimBlankLines(bodyLines.slice(i + 1).join('\n')) };
    }
  }
  return { slug: '', body: trimBlankLines(bodyLines.join('\n')) };
}

export function parseItems(markdown: string): ParsedSection {
  const lines = markdown.split('\n');
  const headings = findHeadings(lines);
  const h2s = headings.filter((h) => h.level === 2);

  if (h2s.length === 0) {
    // No items — return the body untouched (non-destructive freeform fallback).
    return { preamble: markdown, items: [] };
  }

  const preamble = lines.slice(0, h2s[0].line).join('\n');
  const usedIds = new Set<string>();
  const mintId = (name: string): string => {
    const base = slugify(name) || 'item';
    let id = base;
    let n = 2;
    while (usedIds.has(id)) {
      id = `${base}-${n}`;
      n += 1;
    }
    usedIds.add(id);
    return id;
  };

  const items: DesignItem[] = [];
  for (let gi = 0; gi < h2s.length; gi += 1) {
    const start = h2s[gi].line;
    const end = gi + 1 < h2s.length ? h2s[gi + 1].line : lines.length;
    // H3 children within this H2 region.
    const childMarks = headings.filter((h) => h.level === 3 && h.line > start && h.line < end);
    const ownBodyEnd = childMarks.length > 0 ? childMarks[0].line : end;
    const { slug, body } = splitSlugAndBody(lines.slice(start + 1, ownBodyEnd));

    const children: DesignItem[] = [];
    for (let ci = 0; ci < childMarks.length; ci += 1) {
      const cStart = childMarks[ci].line;
      const cEnd = ci + 1 < childMarks.length ? childMarks[ci + 1].line : end;
      const child = splitSlugAndBody(lines.slice(cStart + 1, cEnd));
      children.push({
        id: mintId(childMarks[ci].text),
        name: childMarks[ci].text,
        slug: child.slug,
        body: child.body,
        children: [],
      });
    }

    items.push({ id: mintId(h2s[gi].text), name: h2s[gi].text, slug, body, children });
  }

  return { preamble, items };
}

function serializeItem(item: DesignItem, level: number): string {
  // A blank name would serialise to a bare `## ` line that the parser can't
  // recognise as a heading — the item (and its body) would be silently absorbed
  // into its neighbour on the next parse. Fall back to a placeholder so every
  // item always round-trips as a real heading.
  const name = item.name.trim() || 'Untitled';
  let out = `${'#'.repeat(level)} ${name}`;
  if (item.slug.trim()) out += `\n_${item.slug.trim()}_`;
  const body = trimBlankLines(item.body);
  if (body) out += `\n\n${body}`;
  if (level === 2 && item.children.length > 0) {
    const kids = item.children.map((c) => serializeItem(c, 3)).join('\n\n');
    out += `\n\n${kids}`;
  }
  return out;
}

export function serializeItems(section: ParsedSection): string {
  // No items → return the preamble exactly (the freeform body is untouched).
  if (section.items.length === 0) return section.preamble;
  const pre = section.preamble.replace(/\s+$/, '');
  const body = section.items.map((i) => serializeItem(i, 2)).join('\n\n');
  return `${pre ? `${pre}\n\n` : ''}${body}\n`;
}

/** Build an empty item with a unique-ish id (callers re-key via parse on save). */
export function blankItem(name = 'New item'): DesignItem {
  return { id: slugify(name) || 'item', name, slug: '', body: '', children: [] };
}

const nameKey = (s: string): string => s.trim().toLowerCase();

/** Canonical name of the pinned "core loop" item that always leads the Systems
 *  section — the moment-to-moment loop the whole design hangs off. Every AI
 *  surface (wizard, Co-Designer, Hammer, reconcile, build) treats it as the
 *  spine. See {@link withGameLoopFirst}. */
export const GAME_LOOP_NAME = 'Game Loop';

/** Whether an item is the canonical Game Loop (case-insensitive). */
export function isGameLoopItem(name: string): boolean {
  return name.trim().toLowerCase() === GAME_LOOP_NAME.toLowerCase();
}

/** Float the Game Loop item to the front (preserving the order of the rest), so
 *  it always leads the section. The FIRST Game Loop is canonical (matching the
 *  first-occurrence-wins merge in {@link mergeItemsMarkdown}). Returns the same
 *  array reference when no move is needed, so callers can detect a no-op. */
export function withGameLoopFirst(items: DesignItem[]): DesignItem[] {
  const idx = items.findIndex((it) => isGameLoopItem(it.name));
  if (idx <= 0) return items;
  const copy = items.slice();
  const [loop] = copy.splice(idx, 1);
  copy.unshift(loop);
  return copy;
}

/** Whether a section id is the loop-hosting Systems section (the one that pins
 *  the Game Loop). Tolerates the `gdd-`/`prd-` prefix dual-doc projects use. */
export function isGameLoopSection(sectionId: string): boolean {
  return sectionId.replace(/^(?:gdd|prd)-/, '') === '01-systems';
}

/**
 * Merge an incoming item onto an existing one of the same name. The name is
 * taken from `incoming`; its slug/body update the existing values *unless the
 * incoming leaves them blank*, in which case the existing ones are preserved —
 * so re-emitting a group heading just to ADD `### children` (the shape the
 * co-designer/revise prompts ask for) can't wipe the group's own description or
 * details. Children are upserted *by name* — an incoming child replaces the
 * matching existing child, new children are appended, and existing children the
 * incoming item doesn't mention are preserved.
 */
function mergeItemInto(existing: DesignItem, incoming: DesignItem): DesignItem {
  const slug = incoming.slug.trim() ? incoming.slug : existing.slug;
  const body = incoming.body.trim() ? incoming.body : existing.body;
  if (incoming.children.length === 0) {
    // Incoming didn't address children → keep the existing ones untouched.
    return { ...incoming, slug, body, children: existing.children };
  }
  const indexByName = new Map(existing.children.map((c, i) => [nameKey(c.name), i]));
  const children = [...existing.children];
  for (const ic of incoming.children) {
    const child: DesignItem = { ...ic, children: [] };
    const idx = indexByName.get(nameKey(ic.name));
    if (idx !== undefined) children[idx] = child;
    else {
      indexByName.set(nameKey(ic.name), children.length);
      children.push(child);
    }
  }
  return { ...incoming, slug, body, children };
}

/**
 * Merge `incoming` item markdown into `current`, upserting by name: an incoming
 * item whose name matches an existing one is merged onto it (preserving any
 * children — and any other items — the incoming markdown doesn't mention); new
 * names are appended. Used so the Co-Designer's "add an item" reply augments a
 * section instead of erasing it. Returns `current` unchanged when `incoming`
 * has no items to merge.
 *
 * `opts.pinGameLoop` (Systems only) floats the Game Loop item to the top after
 * merging — off by default so non-Systems sections aren't reordered if they
 * happen to contain an item literally named "Game Loop".
 */
export function mergeItemsMarkdown(
  current: string,
  incoming: string,
  opts: { pinGameLoop?: boolean } = {},
): string {
  const cur = parseItems(current);
  const inc = parseItems(incoming);
  if (inc.items.length === 0) return current;
  // First occurrence wins: a duplicated name updates the SAME item
  // withGameLoopFirst treats as canonical (the leading one), so the pinned
  // Game Loop can't go stale while a trailing copy is the one updated.
  const indexByName = new Map<string, number>();
  cur.items.forEach((it, i) => {
    const k = nameKey(it.name);
    if (!indexByName.has(k)) indexByName.set(k, i);
  });
  const items = [...cur.items];
  for (const it of inc.items) {
    const idx = indexByName.get(nameKey(it.name));
    if (idx !== undefined) items[idx] = mergeItemInto(items[idx], it);
    else {
      indexByName.set(nameKey(it.name), items.length);
      items.push(it);
    }
  }
  return serializeItems({
    preamble: cur.preamble || inc.preamble,
    items: opts.pinGameLoop ? withGameLoopFirst(items) : items,
  });
}

/**
 * Whether an AI reply for an item-mode section actually parsed into items.
 * The merge/apply flows use this to tell apart "the model returned editable
 * item(s)" (merge them) from "the model replied in prose / a plain list" — the
 * latter would otherwise be silently dropped by {@link mergeItemsMarkdown}
 * (which returns the body unchanged when there are no items to merge).
 */
export function replyHasItems(incoming: string): boolean {
  return parseItems(incoming).items.length > 0;
}

/**
 * Safety net for an item-mode reply that isn't item-shaped: capture the raw text
 * as its own clearly-labelled trailing item instead of discarding it. Appending
 * to a new heading (rather than concatenating onto the body) keeps the prose
 * isolated — it round-trips cleanly and never bleeds into an existing card on
 * the next parse. Returns `current` unchanged when `raw` is empty/whitespace.
 */
export function appendFreeformItem(
  current: string,
  raw: string,
  heading = 'Co-Designer notes',
): string {
  const body = raw.trim();
  if (!body) return current;
  const cur = parseItems(current);
  // In a freeform (no-item) section there's nothing to preserve as cards — keep
  // it simple and append the prose to the existing body.
  if (cur.items.length === 0) {
    const base = current.replace(/\s+$/, '');
    return base ? `${base}\n\n${body}\n` : `${body}\n`;
  }
  return serializeItems({
    preamble: cur.preamble,
    items: [
      ...cur.items,
      { id: slugify(heading) || 'notes', name: heading, slug: '', body, children: [] },
    ],
  });
}

/** Identity-free content signature of one item (name + slug + body, sans
 *  children) for change detection. */
function itemSignature(it: DesignItem): string {
  return `${it.name.trim()} ${it.slug.trim()} ${it.body.trim()}`;
}

/**
 * Ids of `current` items/sub-items that differ from `baseline` (the last-built
 * snapshot) — drives the per-item "edited since last build" pips. Items are
 * matched by name (case-insensitive); a parent is flagged when its own content
 * changed, when any child changed, or when its child set was added to / removed
 * from. A child with no baseline match (added or renamed) is flagged.
 */
export function diffChangedItemIds(current: ParsedSection, baseline: ParsedSection): Set<string> {
  const changed = new Set<string>();
  const key = (s: string): string => s.trim().toLowerCase();
  const baseTop = new Map(baseline.items.map((b) => [key(b.name), b]));

  for (const cur of current.items) {
    const base = baseTop.get(key(cur.name));
    if (!base) {
      changed.add(cur.id);
      for (const c of cur.children) changed.add(c.id);
      continue;
    }
    const baseChildren = new Map(base.children.map((b) => [key(b.name), b]));
    let childChanged = false;
    for (const cc of cur.children) {
      const bc = baseChildren.get(key(cc.name));
      if (!bc || itemSignature(cc) !== itemSignature(bc)) {
        changed.add(cc.id);
        childChanged = true;
      }
    }
    const childCountChanged = base.children.length !== cur.children.length;
    if (itemSignature(cur) !== itemSignature(base) || childChanged || childCountChanged) {
      changed.add(cur.id);
    }
  }
  return changed;
}

/**
 * Whether a section should use the item-based editor. The explicit
 * `itemMode` flag wins; absent, default on for the canonical Systems + Content
 * sections so existing projects (scaffolded before the flag existed) light up
 * without a migration.
 */
export function itemModeFor(section: { id: string; itemMode?: boolean }): boolean {
  if (typeof section.itemMode === 'boolean') return section.itemMode;
  return (
    section.id === '01-systems' || section.id === '02-art-direction' || section.id === '03-content'
  );
}
